/**
 * ssoClient.js — SSO corporativo (Microsoft Entra ID) no lado da SPA.
 *
 * Toda a parte sensível do OIDC (client secret, PKCE, troca do code, validação
 * do id_token) vive no backend, em `/auth/sso/*`. A SPA só faz três coisas:
 *
 *   1. fetchAuthConfig()  → pergunta se o SSO está ligado e se o formulário de
 *                           e-mail/senha deve aparecer.
 *   2. redirectToSso()    → navega para o /auth/sso/start (o backend redireciona
 *                           para a Microsoft).
 *   3. consumeCallbackTokens() → lê os tokens do fragmento (#) da URL de retorno
 *                           e LIMPA a URL imediatamente.
 *
 * Por que os tokens chegam no fragmento e não na query:
 *   O fragmento nunca é enviado ao servidor — não aparece em Referer, em log de
 *   proxy nem no access-log do nginx. Uma query `?access_token=` apareceria em
 *   todos eles (OWASP A09).
 *
 * Por que limpamos a URL logo em seguida:
 *   Mesmo no fragmento, o token ficaria visível na barra de endereço e no
 *   histórico do navegador. `history.replaceState` remove sem criar entrada nova.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

/** Rota da SPA que recebe o retorno do backend após o SSO. */
export const SSO_CALLBACK_PATH = '/auth/callback';

/**
 * Consulta ao backend quais formas de login estão disponíveis.
 *
 * Falha de rede não pode travar a tela de login: caímos no comportamento
 * conservador (sem SSO, com formulário de credenciais), que é o modo em que a
 * plataforma sempre funcionou.
 *
 * @returns {Promise<{ ssoEnabled: boolean, credentialsEnabled: boolean, providerLabel: string }>}
 */
export async function fetchAuthConfig() {
  try {
    const response = await fetch(`${BASE_URL}/auth/sso/config`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('CONFIG_UNAVAILABLE');

    const data = await response.json();
    return {
      ssoEnabled:         Boolean(data.sso_enabled),
      credentialsEnabled: data.credentials_enabled !== false,
      providerLabel:      data.provider_label ?? 'Entrar com Microsoft',
    };
  } catch {
    return {
      ssoEnabled:         false,
      credentialsEnabled: true,
      providerLabel:      'Entrar com Microsoft',
    };
  }
}

/**
 * Inicia o fluxo SSO: navegação de página inteira para o backend, que monta o
 * state/PKCE e redireciona para a Microsoft.
 *
 * Precisa ser `window.location.href` (e não fetch): o fluxo OIDC é uma sequência
 * de redirects de navegador, o usuário tem de VER a tela da Microsoft.
 *
 * @param {string} nextPath — rota da SPA para onde voltar após autenticar.
 */
export function redirectToSso(nextPath = '/dashboard') {
  const url = new URL(`${BASE_URL}/auth/sso/start`, window.location.origin);
  url.searchParams.set('next', nextPath);
  window.location.href = url.toString();
}

/**
 * Lê os tokens do fragmento da URL de callback e limpa a barra de endereço.
 *
 * @returns {{ access_token: string, refresh_token: string, expires_in: number, next: string } | null}
 *          null quando não há tokens no fragmento (acesso direto à rota).
 */
export function consumeCallbackTokens() {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!raw) return null;

  const params = new URLSearchParams(raw);
  const accessToken = params.get('access_token');
  if (!accessToken) return null;

  // Remove o fragmento da URL antes de qualquer render — sem entrada nova no
  // histórico, para o botão "voltar" não reexibir o token.
  try {
    window.history.replaceState(null, '', window.location.pathname);
  } catch {
    // Ambientes sem History API: seguimos, o token já está em memória.
  }

  return {
    access_token:  accessToken,
    refresh_token: params.get('refresh_token') ?? '',
    expires_in:    Number(params.get('expires_in')) || 3600,
    next:          params.get('next') || '/dashboard',
  };
}

/**
 * Traduz o código de erro devolvido pelo backend em `/login?error=...`.
 *
 * Mensagens genéricas de propósito: não revelam se o e-mail existe, se o usuário
 * está no tenant nem qual etapa falhou (OWASP A07).
 *
 * @param {string | null} code
 * @returns {string} mensagem pt-BR, ou string vazia se não houver erro.
 */
export function ssoErrorMessage(code) {
  switch (code) {
    case 'sso_cancelled':
      return 'Login pela Microsoft cancelado.';
    case 'sso_state':
      return 'Sua sessão de login expirou. Tente novamente.';
    case 'sso_provision':
      return 'Sua conta ainda não tem acesso à plataforma. Fale com um administrador.';
    case 'sso_failed':
      return 'Não foi possível entrar com a Microsoft. Tente novamente.';
    default:
      return code ? 'Não foi possível entrar. Tente novamente.' : '';
  }
}

/**
 * Renova a sessão de um usuário autenticado por SSO.
 *
 * O refresh do stub (`oidcStub.js`) procura o usuário numa lista fixa de demo —
 * quem entrou por SSO não está lá. Por isso a renovação de uma sessão SSO vai
 * ao backend, que reemite o par a partir do refresh token que ele mesmo assinou.
 *
 * @param {string} refreshToken
 * @returns {Promise<{ access_token: string, refresh_token: string, expires_in: number }>}
 * @throws {Error} INVALID_REFRESH_TOKEN quando a sessão não pode ser renovada.
 */
export async function refreshSsoTokens(refreshToken) {
  const response = await fetch(`${BASE_URL}/auth/sso/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    throw Object.assign(new Error('INVALID_REFRESH_TOKEN'), { status: response.status });
  }
  return response.json();
}

/**
 * Diz se um token foi emitido pelo fluxo SSO.
 *
 * O backend marca o claim `amr: ['sso']` ao emitir — é o que permite escolher
 * entre o refresh do stub (login por credenciais) e o do backend, sem que o
 * AuthContext precise guardar a origem da sessão em lugar nenhum.
 *
 * @param {string} token — JWT (access ou refresh).
 * @returns {boolean}
 */
export function isSsoToken(token) {
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    return Array.isArray(payload.amr) && payload.amr.includes('sso');
  } catch {
    return false;
  }
}
